import { FC, FormEvent, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Autocomplete,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    useMediaQuery,
    useTheme,
    IconButton,
    InputAdornment,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    Save as SaveIcon,
    Person as PersonIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import type { ScheduleItem } from '@/types';
import { useI18nContext } from '@/i18n/i18n-react';
import {
    useGetScheduleTemplatesQuery,
    useGetUsersQuery,
    useGetCategoriesQuery,
    useScheduleSearchQuery,
} from '@/store/apiSlice.ts';
import { createCategoryPlaceholder } from '@/utils/schedulePlaceholders';
import CategoryIcon from '@/components/CategoryIcon.tsx';
import { splitTags, normalizeTags, tagsToString } from '@/utils/tags';

// Combined type for autocomplete options
// (kept local so MUI Autocomplete infers option shape correctly)
type ResponsibleOption =
    | { type: 'person'; name: string }
    | { type: 'category'; name: string; icon: string };

// Title suggestions: templates + previous titles filtered by tags
type TitleOption =
    | { type: 'template'; template: string; duration: number; responsible?: string; tags?: string }
    | {
          type: 'history';
          title: string;
          duration?: number;
          responsible?: string;
          note?: string;
          tags?: string;
      };

interface ScheduleItemDialogProps {
    open: boolean;
    editItem: ScheduleItem | null;
    formData: Omit<ScheduleItem, 'id'>;
    isCreating: boolean;
    isUpdating: boolean;
    onChange: (data: Omit<ScheduleItem, 'id'>) => void;
    onSubmit: (e: FormEvent) => void;
    onCancel: () => void;
}

const ScheduleItemDialog: FC<ScheduleItemDialogProps> = ({
    open,
    editItem,
    formData,
    isCreating,
    isUpdating,
    onChange,
    onSubmit,
    onCancel,
}) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: scheduleTemplates } = useGetScheduleTemplatesQuery(undefined, {
        skip: !open,
    });
    const { data: users } = useGetUsersQuery();
    const { data: categories } = useGetCategoriesQuery();

    const responsibleOptions: ResponsibleOption[] = useMemo(
        () => [
            ...(categories?.map((cat) => ({
                type: 'category' as const,
                name: cat.name,
                icon: cat.icon,
            })) || []),
            ...(users?.map((user) => ({ type: 'person' as const, name: user.name })) || []),
        ],
        [categories, users]
    );

    const tagsForSearch = useMemo(() => {
        // send normalized tags to backend for better hit rate (whitespace/case)
        const normalized = normalizeTags(formData.tags);
        return normalized.join(',');
    }, [formData.tags]);

    const shouldSearchHistory = open && tagsForSearch.length > 0;

    const { data: searchResult } = useScheduleSearchQuery(
        {
            tags: tagsForSearch,
            title: formData.title,
            limit: 50,
        },
        { skip: !shouldSearchHistory || !formData.title }
    );

    const historicalTitleOptions: TitleOption[] = useMemo(() => {
        const values = searchResult?.values ?? [];

        const items = values
            .map((v) => {
                if (typeof v === 'string') {
                    const title = (v as string).trim();
                    if (!title) return null;
                    return { title };
                }

                if (v && typeof v === 'object') {
                    const anyV = v as any;
                    const title = String(anyV.title ?? '').trim();
                    if (!title) return null;
                    return {
                        title,
                        duration:
                            typeof anyV.duration === 'number'
                                ? anyV.duration
                                : anyV.duration
                                  ? parseInt(anyV.duration, 10)
                                  : undefined,
                        responsible:
                            typeof anyV.responsible === 'string' ? anyV.responsible : undefined,
                        note: typeof anyV.note === 'string' ? anyV.note : undefined,
                        tags: typeof anyV.tags === 'string' ? anyV.tags : undefined,
                    };
                }

                return null;
            })
            .filter(Boolean) as Array<{
            title: string;
            duration?: number;
            responsible?: string;
            note?: string;
            tags?: string;
        }>;

        // Avoid suggesting the exact currently-entered title as a "history" option.
        const current = (formData.title ?? '').trim();

        // Deduplicate by title (keep the first instance with details)
        const map = new Map<string, (typeof items)[number]>();
        for (const it of items) {
            if (!map.has(it.title)) map.set(it.title, it);
        }

        return Array.from(map.values())
            .filter((it) => it.title !== current)
            .map((it) => ({ type: 'history' as const, ...it }));
    }, [searchResult?.values, formData.title]);

    const titleOptions: TitleOption[] = useMemo(() => {
        const templateOptions: TitleOption[] = (scheduleTemplates ?? []).map((t) => ({
            type: 'template' as const,
            template: t.template,
            duration: t.duration,
            responsible: t.responsible,
            tags: t.tags,
        }));
        return [...templateOptions, ...historicalTitleOptions];
    }, [scheduleTemplates, historicalTitleOptions]);

    const tagSuggestions = useMemo(() => {
        const set = new Set<string>();
        for (const t of scheduleTemplates ?? []) {
            for (const tag of normalizeTags(t.tags)) set.add(tag);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [scheduleTemplates]);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        onSubmit(e);
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 }, pt: { xs: 1.5, sm: 2 } }}>
                {editItem
                    ? LL.scheduleModal.editScheduleItem()
                    : LL.scheduleModal.addScheduleItem()}
            </DialogTitle>
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 2 } }}>
                <Box component="form" id="schedule-item-form" onSubmit={handleSubmit}>
                    <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1, sm: 2 } }}>
                        <Autocomplete
                            freeSolo
                            options={titleOptions}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.type === 'template'
                                    ? `${option.template} (${option.duration}m)`
                                    : option.title;
                            }}
                            value={formData.title}
                            onChange={(_, newValue) => {
                                if (typeof newValue === 'object' && newValue !== null) {
                                    if (newValue.type === 'template') {
                                        onChange({
                                            ...formData,
                                            title: newValue.template,
                                            duration: newValue.duration,
                                            responsible:
                                                newValue.responsible || formData.responsible,
                                            tags: newValue.tags ?? formData.tags ?? '',
                                        });
                                    } else {
                                        onChange({
                                            ...formData,
                                            title: newValue.title,
                                            duration:
                                                newValue.duration !== undefined
                                                    ? newValue.duration
                                                    : formData.duration,
                                            responsible:
                                                newValue.responsible ?? formData.responsible,
                                            note: newValue.note ?? formData.note,
                                            tags: newValue.tags ?? formData.tags,
                                        });
                                    }
                                } else {
                                    onChange({ ...formData, title: newValue || '' });
                                }
                            }}
                            onInputChange={(_, newInputValue) => {
                                onChange({ ...formData, title: newInputValue });
                            }}
                            renderOption={(props, option) => {
                                if (typeof option === 'string') {
                                    return (
                                        <ListItem {...props} key={option}>
                                            <ListItemText primary={option} />
                                        </ListItem>
                                    );
                                }
                                if (option.type === 'template') {
                                    const tags = normalizeTags(option.tags);
                                    return (
                                        <ListItem {...props} key={`template-${option.template}`}>
                                            <Stack sx={{ width: '100%' }} spacing={0.5}>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.75}
                                                    sx={{
                                                        alignItems: 'center',
                                                        flexWrap: 'wrap',
                                                        rowGap: 0.5,
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            fontSize: '0.95rem',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {option.template}
                                                    </Box>
                                                    {tags.map((tag) => (
                                                        <Chip
                                                            key={tag}
                                                            label={tag}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 20 }}
                                                        />
                                                    ))}
                                                </Stack>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {`${LL.scheduleModal.fromTemplate()} • ${option.duration}m`}
                                                </Box>
                                            </Stack>
                                        </ListItem>
                                    );
                                }
                                return (
                                    <ListItem {...props} key={`history-${option.title}`}>
                                        <ListItemText
                                            primary={option.title}
                                            secondary={LL.scheduleModal.fromHistory()}
                                        />
                                    </ListItem>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={LL.scheduleModal.titleLabel()}
                                    required
                                    slotProps={{
                                        ...params.slotProps,
                                        htmlInput: {
                                            ...params.slotProps.htmlInput,
                                            maxLength: 255,
                                        },
                                    }}
                                    autoFocus
                                    size={isMobile ? 'small' : 'medium'}
                                    helperText={
                                        !isMobile
                                            ? LL.scheduleModal.titleHelperTextWithTags()
                                            : undefined
                                    }
                                />
                            )}
                        />
                        <TextField
                            label={LL.scheduleModal.duration()}
                            type="number"
                            value={formData.duration || ''}
                            onChange={(e) =>
                                onChange({
                                    ...formData,
                                    duration: e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : undefined,
                                })
                            }
                            slotProps={{ htmlInput: { min: 0 } }}
                            fullWidth
                            required
                            size={isMobile ? 'small' : 'medium'}
                            helperText={
                                !isMobile ? LL.scheduleModal.durationHelperText() : undefined
                            }
                        />
                        <Autocomplete
                            freeSolo
                            options={responsibleOptions}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.name
                            }
                            value={formData.responsible}
                            groupBy={(option) =>
                                typeof option === 'string'
                                    ? ''
                                    : option.type === 'person'
                                      ? LL.scheduleModal.person()
                                      : LL.scheduleModal.category()
                            }
                            onChange={(_, newValue) => {
                                if (typeof newValue === 'object' && newValue !== null) {
                                    const valueToAdd =
                                        newValue.type === 'category'
                                            ? createCategoryPlaceholder(newValue.name)
                                            : newValue.name;
                                    onChange({
                                        ...formData,
                                        responsible: valueToAdd,
                                    });
                                } else {
                                    onChange({ ...formData, responsible: newValue || '' });
                                }
                            }}
                            onInputChange={(_, newInputValue) => {
                                onChange({ ...formData, responsible: newInputValue });
                            }}
                            renderOption={(props, option) => {
                                if (typeof option === 'string') {
                                    return (
                                        <ListItem {...props} key={option}>
                                            <ListItemText primary={option} />
                                        </ListItem>
                                    );
                                }
                                return (
                                    <ListItem {...props} key={`${option.type}-${option.name}`}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {option.type === 'person' ? (
                                                <PersonIcon fontSize="small" color="primary" />
                                            ) : (
                                                <CategoryIcon
                                                    size="1.25rem"
                                                    category={option.icon}
                                                />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText primary={option.name} />
                                    </ListItem>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={LL.scheduleModal.responsiblePerson()}
                                    required
                                    slotProps={{
                                        ...params.slotProps,
                                        htmlInput: {
                                            ...params.slotProps.htmlInput,
                                            maxLength: 255,
                                        },

                                        input: {
                                            ...params.slotProps.input,
                                            endAdornment: (
                                                <>
                                                    {formData.responsible && (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    onChange({
                                                                        ...formData,
                                                                        responsible: '',
                                                                    })
                                                                }
                                                                edge="end"
                                                                sx={{ mr: -0.5 }}
                                                            >
                                                                <ClearIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )}
                                                    {params.slotProps.input.endAdornment}
                                                </>
                                            ),
                                        },
                                    }}
                                    size={isMobile ? 'small' : 'medium'}
                                    helperText={
                                        !isMobile
                                            ? LL.scheduleModal.responsibleHelperText({
                                                  placeholder: '{{categoryName}}',
                                              })
                                            : undefined
                                    }
                                />
                            )}
                        />
                        <TextField
                            label={LL.scheduleModal.notes()}
                            value={formData.note}
                            onChange={(e) => onChange({ ...formData, note: e.target.value })}
                            multiline
                            rows={isMobile ? 2 : 3}
                            fullWidth
                            size={isMobile ? 'small' : 'medium'}
                        />
                        {/* Tags (chips) */}
                        <Autocomplete
                            multiple
                            freeSolo
                            options={tagSuggestions}
                            value={splitTags(formData.tags)}
                            onChange={(_e, newValue) => {
                                onChange({
                                    ...formData,
                                    tags: tagsToString(newValue as string[]),
                                });
                            }}
                            renderValue={(value: readonly string[], getItemProps) =>
                                value.map((option: string, index: number) => (
                                    <Chip
                                        variant="outlined"
                                        size="small"
                                        label={option}
                                        {...getItemProps({ index })}
                                        key={`${option}-${index}`}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={LL.scheduleModal.tagsLabel()}
                                    placeholder={LL.scheduleModal.tagsPlaceholder()}
                                    size={isMobile ? 'small' : 'medium'}
                                    helperText={
                                        !isMobile ? LL.scheduleModal.tagsHelperText() : undefined
                                    }
                                />
                            )}
                        />
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 1.5 }, gap: { xs: 0.5, sm: 1 } }}
            >
                <Button
                    onClick={onCancel}
                    disabled={isCreating || isUpdating}
                    size={isMobile ? 'small' : 'medium'}
                >
                    {LL.common.cancel()}
                </Button>
                <Button
                    type="submit"
                    form="schedule-item-form"
                    variant="contained"
                    startIcon={editItem ? <SaveIcon /> : <AddIcon />}
                    disabled={isCreating || isUpdating}
                    size={isMobile ? 'small' : 'medium'}
                >
                    {editItem ? LL.common.save() : LL.common.add()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleItemDialog;
